import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Badge,
  Box,
  Card,
  Divider,
  Flex,
  Heading,
  Icon,
  SimpleGrid,
  Skeleton,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import CustomButton from "../../components/ButtonComp";
import { BiArrowBack } from "react-icons/bi";
import ImageModal from "../../components/ImageModal";
import { formatCurrency } from "../../utils/utils";
import CustomDataCard from "../../components/CustomDataCard";
import {
  FiShoppingCart,
  FiFileText,
  FiUser,
  FiCalendar,
  FiDollarSign,
  FiHash,
  FiPackage,
} from "react-icons/fi";

// ── Section Header ─────────────────────────────────────────────────────────────
const SectionHeader = ({ icon, title, color = "blue.500", count }) => (
  <Flex align="center" gap={3} mb={4}>
    <Box
      w="34px"
      h="34px"
      borderRadius="9px"
      bg={`${color.split(".")[0]}.50`}
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
    >
      <Icon as={icon} color={color} boxSize={4} />
    </Box>
    <Flex align="center" gap={2}>
      <Text fontWeight="700" fontSize="md" color="gray.700">
        {title}
      </Text>
      {count != null && (
        <Badge colorScheme="blue" borderRadius="8px" px={2} fontSize="xs">
          {count}
        </Badge>
      )}
    </Flex>
  </Flex>
);

// ── Info Field ─────────────────────────────────────────────────────────────────
const InfoField = ({ label, value, highlight, valueColor }) => (
  <Box>
    <Text
      fontSize="10px"
      fontWeight="700"
      color="gray.400"
      textTransform="uppercase"
      letterSpacing="0.6px"
      mb="3px"
    >
      {label}
    </Text>
    <Text
      fontSize="sm"
      fontWeight={highlight ? "700" : "500"}
      color={valueColor || (highlight ? "blue.600" : "gray.800")}
      wordBreak="break-word"
    >
      {value || "N/A"}
    </Text>
  </Box>
);

// ── Summary Stat Tile ──────────────────────────────────────────────────────────
const StatTile = ({ label, value, icon, accentColor }) => (
  <Box
    bg="white"
    borderRadius="12px"
    border="1.5px solid"
    borderColor="gray.100"
    borderLeft="4px solid"
    borderLeftColor={accentColor}
    p={4}
    boxShadow="0 1px 4px rgba(0,0,0,0.06)"
  >
    <Flex align="center" gap={3}>
      <Box
        w="36px"
        h="36px"
        borderRadius="9px"
        bg={`${accentColor.split(".")[0]}.50`}
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
      >
        <Icon as={icon} color={accentColor} boxSize={4} />
      </Box>
      <Box>
        <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.6px" mb="2px">
          {label}
        </Text>
        <Text fontSize="md" fontWeight="700" color="gray.800">
          {value || "N/A"}
        </Text>
      </Box>
    </Flex>
  </Box>
);

// ── Main Component ─────────────────────────────────────────────────────────────
const PurchaseDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { onOpen, onClose, isOpen } = useDisclosure();
  const [url, setUrl] = useState("");

  const purchaseData = location?.state || {};

  // Product card data builder
  const createProductCardData = (item) => [
    { key: "Product Name", value: item.product_name, valueWeight: "semibold" },
    { key: "Quantity", value: item.quantity, valueWeight: "medium" },
    { key: "Unit Price", value: formatCurrency(item.unit_price), valueWeight: "medium" },
    { key: "GST", value: item.gst + "%", valueWeight: "medium" },
    { key: "GST Amount", value: formatCurrency(item.gst_amount), valueWeight: "medium" },
    { key: "Taxable Amount", value: formatCurrency(item.taxable_amount), valueWeight: "medium" },
    { key: "Discount", value: formatCurrency(item.discount), valueWeight: "medium" },
    { key: "Empty Returned", value: item.empty_returned, valueWeight: "medium" },
  ];

  return (
    <>
      <Card
        display="flex"
        flexDirection="row"
        alignItems="center"
        justify="space-between"
        paddingInline={5}
        paddingBlock={3}
        boxShadow="sm"
        borderRadius="12px"
        mb={1}
      >
        <Flex align="center" gap={3}>
          <Box
            w="36px"
            h="36px"
            borderRadius="9px"
            bg="blue.50"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={FiShoppingCart} color="blue.500" boxSize={4} />
          </Box>
          <Box>
            <Heading fontSize="lg" color="gray.800">
              Purchase Details
            </Heading>
            {purchaseData?.inv_no && (
              <Text fontSize="xs" color="gray.400">
                Invoice: {purchaseData.inv_no}
              </Text>
            )}
          </Box>
        </Flex>
        <CustomButton
          icon={<BiArrowBack color="#fff" />}
          title="Back"
          height="34px"
          b_color="#374151"
          onClick={() =>
            navigate("/purchase", {
              state: {
                fromList: true,
                page: location.state?.page || 1,
                rows: location.state?.rows || 5,
                initialValue: location.state?.initialValue || 1,
              },
            })
          }
        />
      </Card>

      <VStack spacing={4} align="stretch" mt={4}>

        <SimpleGrid columns={[2, 2, 4]} spacing={3}>
          <StatTile
            label="INV Type"
            value={purchaseData?.type}
            icon={FiFileText}
            accentColor="green.500"
          />
          <StatTile
            label="Total Amount"
            value={formatCurrency(purchaseData?.total_amount)}
            icon={FiDollarSign}
            accentColor="blue.500"
          />
          <StatTile
            label="Vendor ID"
            value={purchaseData?.vendor_id}
            icon={FiUser}
            accentColor="purple.500"
          />
          <StatTile
            label="INV Date"
            value={purchaseData?.inv_date}
            icon={FiCalendar}
            accentColor="orange.400"
          />
        </SimpleGrid>

        <Card
          bg="white"
          borderRadius="14px"
          boxShadow="0 2px 8px rgba(0,0,0,0.06)"
          border="1.5px solid"
          borderColor="gray.100"
          p={6}
        >
          <SectionHeader icon={FiFileText} title="Invoice Information" color="green.500" />
          <SimpleGrid columns={[1, 2, 3]} spacing={5}>
            <InfoField
              label="Invoice Type"
              value={purchaseData?.type}
              valueColor="green.600"
              highlight
            />
            <InfoField label="Invoice Number" value={purchaseData?.inv_no} />
            <InfoField label="Invoice Date" value={purchaseData?.inv_date} />
            <InfoField label="Vendor ID" value={purchaseData?.vendor_id} />
            <InfoField
              label="Total Amount"
              value={formatCurrency(purchaseData?.total_amount)}
              valueColor="blue.600"
              highlight
            />
          </SimpleGrid>
        </Card>

        {/* ── Products Grid ─────────────────────────────────────────────────── */}
        {purchaseData?.details && purchaseData.details.length > 0 && (
          <Box>
            <Flex align="center" gap={2} mb={3}>
              <Icon as={FiPackage} color="blue.500" boxSize={4} />
              <Text fontWeight="700" fontSize="md" color="gray.700">
                Products
              </Text>
              <Badge colorScheme="blue" borderRadius="8px" px={2} fontSize="xs">
                {purchaseData.details.length}
              </Badge>
            </Flex>
            <SimpleGrid minChildWidth="280px" spacing={4}>
              {purchaseData.details.map((el, i) => (
                <CustomDataCard
                  key={el.id || i}
                  data={createProductCardData(el)}
                  keyWidth="130px"
                  keyColor="gray.500"
                  keyWeight="medium"
                  valueColor="gray.800"
                  valueWeight="semibold"
                  valueAlign="left"
                  shadow="sm"
                  borderRadius="14px"
                  borderColor="gray.100"
                  borderWidth="1.5px"
                  padding={5}
                  hoverEffect
                  minH="auto"
                  spacing={0}
                />
              ))}
            </SimpleGrid>
          </Box>
        )}

        {/* ── Empty Products ────────────────────────────────────────────────── */}
        {(!purchaseData?.details || purchaseData.details.length === 0) && (
          <Card
            bg="white"
            borderRadius="14px"
            border="1.5px dashed"
            borderColor="gray.200"
            p={10}
            textAlign="center"
          >
            <Icon as={FiPackage} color="gray.200" boxSize={10} mb={3} mx="auto" />
            <Text fontSize="sm" color="gray.400" fontWeight="500">
              No products found for this purchase
            </Text>
          </Card>
        )}

      </VStack>

      <ImageModal url={url} onClose={onClose} isOpen={isOpen} />
    </>
  );
};

export default PurchaseDetail;
